import { useMutation, useQuery } from '@tanstack/react-query'
import type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query'
import type { AxiosResponse } from 'axios'

import { getAdminOrgScope } from '#/shared/api/admin-org-scope'
import type { GetActiveLoadboardsApiV1LoadboardsActiveGet200Item } from './models'
import { serviceClient } from '#/shared/api/http-client'

const BASE = '/shipment/admin/api/v1/product'

/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyResponse = AxiosResponse<any>
type QueryOpts = { query?: Partial<UseQueryOptions<AnyResponse>> & { enabled?: boolean } }
type MutationOpts<V> = { mutation?: UseMutationOptions<AnyResponse, unknown, V> }

function orgParams(): Record<string, unknown> {
  const organization_id = getAdminOrgScope()
  return organization_id ? { organization_id } : {}
}

export function useGetActiveLoadboardsApiV1LoadboardsActiveGet(options?: QueryOpts) {
  const params = orgParams()
  return useQuery<AxiosResponse<GetActiveLoadboardsApiV1LoadboardsActiveGet200Item[]>>({
    queryKey: [`/api/v1/loadboards/active`, params],
    queryFn: () => serviceClient.get(`${BASE}/loadboards/active`, { params }),
    ...options?.query,
  })
}

export function useGetDefaultLoadboardApiV1LoadboardsDefaultGet(options?: QueryOpts) {
  const params = orgParams()
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/loadboards/default`, params],
    queryFn: () => serviceClient.get(`${BASE}/loadboards/default`, { params }),
    ...options?.query,
  })
}

export function useCreateDispatch(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/dispatches`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useGetMarketRate(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/market-rate`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useGetCarrier(carrierId: string, options?: QueryOpts) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/cd/carriers/${carrierId}`],
    queryFn: () => serviceClient.get(`${BASE}/cd/carriers/${carrierId}`, { params: orgParams() }),
    ...options?.query,
  })
}

export function useSearchCarriers(params?: Record<string, unknown>, options?: QueryOpts) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/cd/carriers/search`, params],
    queryFn: () =>
      serviceClient.get(`${BASE}/cd/carriers/search`, { params: { ...orgParams(), ...params } }),
    ...options?.query,
  })
}
